/**
 * Chat Persistence Hooks - React Query v3
 * Hooks for fetching and mutating chat data from trd-agent backend
 */

import { useQuery, useMutation, useQueryClient } from "react-query";
import { chatClient } from "@/utils/chat-client";
import { useToast } from "@/hooks/use-toast";
import type { Conversation } from "@/types/chat.types";

/**
 * Query Keys for React Query cache management
 */
export const chatKeys = {
  all: ["chat"] as const,
  conversations: () => [...chatKeys.all, "conversations"] as const,
  conversation: (id: string) => [...chatKeys.all, "conversation", id] as const,
  messages: (conversationId: string) =>
    [...chatKeys.all, "messages", conversationId] as const,
};

/**
 * Hook to fetch all conversations
 * ✅ React Query is the source of truth - no Zustand sync needed
 */
export const useConversations = () => {
  return useQuery({
    queryKey: chatKeys.conversations(),
    queryFn: async () => {
      const conversations = await chatClient.getConversations();
      return conversations;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Hook to fetch a specific conversation with messages
 * Returns null if no conversationId provided
 * ✅ SIMPLE: Only fetches when conversationId is provided (not null)
 */
export const useConversationSuspense = (conversationId: string | null) => {
  // ✅ Always call the hook, but use enabled to control execution
  const { data } = useQuery({
    queryKey: chatKeys.conversation(conversationId || "__empty__"),
    queryFn: async () => {
      if (!conversationId) {
        return null;
      }

      const data = await chatClient.getConversation(conversationId);

      // ✅ Transform messages to AI SDK v6 format
      const transformedMessages = data.messages
        .filter((msg: any) => {
          // ✅ Filter out assistant messages with empty content (incomplete responses)
          if (msg.role === "assistant") {
            // Handle string content
            if (typeof msg.content === "string" && msg.content.trim() === "") {
              return false;
            }
            // Handle array content
            if (Array.isArray(msg.content) && msg.content.length === 0) {
              return false;
            }
            // Handle no content
            if (!msg.content && !msg.parts) {
              return false;
            }
          }
          return true;
        })
        .map((msg: any, idx) => {
          // ✅ Try to get timestamp from various possible field names
          const timestamp =
            msg.createdAt ||
            msg.created_at ||
            msg.timestamp ||
            msg.date ||
            msg.createdDate;

          // Log if timestamp is missing from backend
          if (!timestamp) {
            console.warn(
              "⚠️ Message from backend missing timestamp, using current time:",
              { id: msg.id, role: msg.role, availableFields: Object.keys(msg) }
            );
          }

          const baseMessage = {
            id: msg.id || `msg-${conversationId}-${idx}`,
            role: msg.role,
            createdAt: timestamp || new Date().toISOString(),
          };

          // If message has content (backend format), convert to parts format
          if (msg.content && !msg.parts) {
            // Content is array - use directly as parts
            if (Array.isArray(msg.content)) {
              return {
                ...baseMessage,
                content: "",
                parts: msg.content,
              };
            }
            // Content is string - convert to parts
            if (typeof msg.content === "string") {
              return {
                ...baseMessage,
                content: msg.content,
                parts: [
                  {
                    type: "text",
                    text: msg.content,
                  },
                ],
              };
            }
          }

          return {
            ...baseMessage,
            content: typeof msg.content === "string" ? msg.content : "",
            parts: msg.parts || [],
          };
        });

      return {
        ...data,
        messages: transformedMessages,
      };
    },
    enabled: !!conversationId, // ✅ Only fetch when conversationId exists
    staleTime: 0, // ✅ Always consider data stale - fetch fresh on every click
    refetchOnMount: "always", // ✅ Always refetch when component mounts (user clicks conversation)
    cacheTime: 1000 * 60, // Keep in memory for 1 minute for quick transitions (v3: cacheTime)
  });

  return data || null;
};

/**
 * Hook to fetch a specific conversation with messages (legacy non-suspense version)
 */
export const useConversation = (conversationId: string | null) => {
  return useQuery({
    queryKey: chatKeys.conversation(conversationId || ""),
    queryFn: async () => {
      if (!conversationId) return null;

      const data = await chatClient.getConversation(conversationId);

      // Transform messages to AI SDK v6 format
      const transformedMessages = data.messages
        .filter((msg: any) => {
          // Filter out assistant messages with empty content
          if (msg.role === "assistant") {
            // Handle string content
            if (typeof msg.content === "string" && msg.content.trim() === "") {
              return false;
            }
            // Handle array content
            if (Array.isArray(msg.content) && msg.content.length === 0) {
              return false;
            }
            // Handle no content
            if (!msg.content && !msg.parts) {
              return false;
            }
          }
          return true;
        })
        .map((msg: any, idx) => {
          // ✅ Try to get timestamp from various possible field names
          const timestamp =
            msg.createdAt ||
            msg.created_at ||
            msg.timestamp ||
            msg.date ||
            msg.createdDate;

          // Log if timestamp is missing from backend
          if (!timestamp) {
            console.warn(
              "⚠️ Message from backend missing timestamp, using current time:",
              { id: msg.id, role: msg.role, availableFields: Object.keys(msg) }
            );
          }

          const baseMessage = {
            id: msg.id || `msg-${conversationId}-${idx}`,
            role: msg.role,
            createdAt: timestamp || new Date().toISOString(),
          };

          if (msg.content && !msg.parts) {
            // Content is array - use directly as parts
            if (Array.isArray(msg.content)) {
              return {
                ...baseMessage,
                content: "",
                parts: msg.content,
              };
            }
            // Content is string - convert to parts
            if (typeof msg.content === "string") {
              return {
                ...baseMessage,
                content: msg.content,
                parts: [{ type: "text", text: msg.content }],
              };
            }
          }

          return {
            ...baseMessage,
            content: typeof msg.content === "string" ? msg.content : "",
            parts: msg.parts || [],
          };
        });

      return {
        ...data,
        messages: transformedMessages,
      };
    },
    enabled: !!conversationId,
    staleTime: 0, // ✅ Always consider data stale - fetch fresh on every click
    refetchOnMount: "always", // ✅ Always refetch when component mounts (user clicks conversation)
    cacheTime: 1000 * 60, // Keep in memory for 1 minute for quick transitions (v3: cacheTime)
  });
};

/**
 * Hook to fetch messages for a conversation
 */
export const useConversationMessages = (conversationId: string | null) => {
  return useQuery({
    queryKey: chatKeys.messages(conversationId || ""),
    queryFn: async () => {
      if (!conversationId) return [];
      const data = await chatClient.getConversation(conversationId);
      return data.messages;
    },
    enabled: !!conversationId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
};

/**
 * Hook to delete a conversation
 * ✅ Only invalidates React Query cache - no Zustand manipulation
 */
export const useDeleteConversation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (conversationId: string) => {
      await chatClient.deleteConversation(conversationId);
    },
    onSuccess: (_, conversationId) => {
      // Invalidate queries - React Query handles the rest
      queryClient.invalidateQueries({
        queryKey: chatKeys.conversations(),
      });
      queryClient.removeQueries({
        queryKey: chatKeys.conversation(conversationId),
      });

      toast({
        title: "Conversación eliminada",
        description: "La conversación se eliminó correctamente",
        variant: "success",
        duration: 3000,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Ocurrió un error al eliminar la conversación",
        variant: "error",
        duration: 5000,
      });
      console.error("Error deleting conversation:", error);
    },
  });
};

/**
 * Hook to update conversation metadata (e.g., title)
 */
export const useUpdateConversation = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      conversationId,
      updates,
    }: {
      conversationId: string;
      updates: Partial<Conversation>;
    }) => {
      return await chatClient.updateConversation(conversationId, updates);
    },
    onSuccess: (updatedConversation, { conversationId }) => {
      // ✅ Update React Query cache (single source of truth)
      queryClient.setQueryData(
        chatKeys.conversation(conversationId),
        (old: any) => {
          if (!old) return old;
          return { ...old, ...updatedConversation };
        }
      );

      // Invalidate conversations list
      queryClient.invalidateQueries({
        queryKey: chatKeys.conversations(),
      });

      toast({
        title: "Conversación actualizada",
        variant: "success",
        duration: 2000,
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Ocurrió un error al actualizar la conversación",
        variant: "error",
        duration: 5000,
      });
      console.error("Error updating conversation:", error);
    },
  });
};

/**
 * Note: useCreateConversation was removed
 * New conversations are now created automatically by the backend
 * when the first message is sent. To start a new conversation,
 * simply set activeConversationId to null in the store.
 */
